import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  HelpdeskTicket,
  TicketStatus,
  TicketPriority,
  TicketType,
  TicketSource,
  TicketComment,
} from '@/models/helpdesk-ticket.model';

const TICKETS_COLLECTION = 'helpdeskTickets';
const COMMENTS_COLLECTION = 'ticketComments';

export class HelpdeskService {
  /**
   * Generate ticket number
   */
  private static generateTicketNumber(): string {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `TKT-${year}-${random}`;
  }

  /**
   * Get all tickets
   */
  static async getAllTickets(): Promise<HelpdeskTicket[]> {
    const q = query(
      collection(db, TICKETS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return Promise.all(
      snapshot.docs.map(doc => this.docToTicket(doc.id, doc.data()))
    );
  }

  /**
   * Get ticket by ID
   */
  static async getTicketById(id: string): Promise<HelpdeskTicket | null> {
    const docRef = doc(db, TICKETS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    const ticket = await this.docToTicket(docSnap.id, docSnap.data());
    
    // Load comments
    const commentsQuery = query(
      collection(db, COMMENTS_COLLECTION),
      where('ticketId', '==', id),
      orderBy('createdAt', 'asc')
    );
    const commentsSnapshot = await getDocs(commentsQuery);
    ticket.comments = commentsSnapshot.docs.map(doc =>
      this.docToComment(doc.id, doc.data())
    );
    
    return ticket;
  }

  /**
   * Subscribe to tickets changes
   */
  static subscribeToTickets(
    callback: (tickets: HelpdeskTicket[]) => void
  ): () => void {
    const q = query(
      collection(db, TICKETS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, async (snapshot: QuerySnapshot) => {
      const tickets = await Promise.all(
        snapshot.docs.map(doc => this.docToTicket(doc.id, doc.data()))
      );
      callback(tickets);
    });
  }

  /**
   * Create ticket
   */
  static async createTicket(ticketData: Partial<HelpdeskTicket>): Promise<string> {
    const ticketNumber = this.generateTicketNumber();
    const now = Timestamp.now();
    
    const data: any = {
      ticketNumber,
      title: ticketData.title || '',
      description: ticketData.description || '',
      type: ticketData.type || TicketType.other,
      priority: ticketData.priority || TicketPriority.medium,
      status: TicketStatus.open,
      source: ticketData.source || TicketSource.member,
      createdBy: ticketData.createdBy || '',
      createdByName: ticketData.createdByName || null,
      createdByEmail: ticketData.createdByEmail || null,
      assignedTo: ticketData.assignedTo || null,
      assignedToName: ticketData.assignedToName || null,
      relatedEmployerId: ticketData.relatedEmployerId || null,
      relatedMemberId: ticketData.relatedMemberId || null,
      relatedProviderId: ticketData.relatedProviderId || null,
      relatedReceiptId: ticketData.relatedReceiptId || null,
      tags: ticketData.tags || [],
      metadata: ticketData.metadata || {},
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, TICKETS_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Update ticket
   */
  static async updateTicket(
    ticketId: string,
    updates: Partial<HelpdeskTicket>
  ): Promise<void> {
    const docRef = doc(db, TICKETS_COLLECTION, ticketId);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.status !== undefined) {
      updateData.status = updates.status;
      if (updates.status === TicketStatus.resolved && !updates.resolvedAt) {
        updateData.resolvedAt = Timestamp.now();
      }
      if (updates.status === TicketStatus.closed && !updates.closedAt) {
        updateData.closedAt = Timestamp.now();
      }
    }
    if (updates.assignedTo !== undefined) updateData.assignedTo = updates.assignedTo;
    if (updates.assignedToName !== undefined) updateData.assignedToName = updates.assignedToName;
    if (updates.tags !== undefined) updateData.tags = updates.tags;

    await updateDoc(docRef, updateData);
  }

  /**
   * Add comment to ticket
   */
  static async addComment(
    ticketId: string,
    comment: Omit<TicketComment, 'id' | 'ticketId' | 'createdAt'>
  ): Promise<string> {
    const now = Timestamp.now();
    
    // Check if this is the first staff response
    const ticket = await this.getTicketById(ticketId);
    if (ticket && !ticket.firstResponseAt && !comment.isInternal) {
      await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
        firstResponseAt: now,
        lastResponseAt: now,
      });
    } else if (ticket && !comment.isInternal) {
      await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
        lastResponseAt: now,
      });
    }

    const data: any = {
      ticketId,
      authorId: comment.authorId,
      authorName: comment.authorName,
      authorEmail: comment.authorEmail || null,
      isInternal: comment.isInternal || false,
      content: comment.content,
      attachments: comment.attachments || [],
      createdAt: now,
    };

    const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Assign ticket
   */
  static async assignTicket(
    ticketId: string,
    assignedTo: string,
    assignedToName: string
  ): Promise<void> {
    await this.updateTicket(ticketId, { assignedTo, assignedToName });
  }

  /**
   * Close ticket
   */
  static async closeTicket(ticketId: string): Promise<void> {
    await this.updateTicket(ticketId, {
      status: TicketStatus.closed,
      closedAt: new Date(),
    });
  }

  /**
   * Convert Firestore document to HelpdeskTicket
   */
  private static async docToTicket(id: string, data: any): Promise<HelpdeskTicket> {
    return {
      id,
      ticketNumber: data.ticketNumber || '',
      title: data.title || '',
      description: data.description || '',
      type: data.type || TicketType.other,
      priority: data.priority || TicketPriority.medium,
      status: data.status || TicketStatus.open,
      source: data.source || TicketSource.member,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName,
      createdByEmail: data.createdByEmail,
      assignedTo: data.assignedTo,
      assignedToName: data.assignedToName,
      relatedEmployerId: data.relatedEmployerId,
      relatedMemberId: data.relatedMemberId,
      relatedProviderId: data.relatedProviderId,
      relatedReceiptId: data.relatedReceiptId,
      comments: [], // Will be loaded separately
      tags: data.tags || [],
      metadata: data.metadata || {},
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      resolvedAt: data.resolvedAt?.toDate(),
      closedAt: data.closedAt?.toDate(),
      firstResponseAt: data.firstResponseAt?.toDate(),
      lastResponseAt: data.lastResponseAt?.toDate(),
    };
  }

  /**
   * Convert Firestore document to TicketComment
   */
  private static docToComment(id: string, data: any): TicketComment {
    return {
      id,
      ticketId: data.ticketId || '',
      authorId: data.authorId || '',
      authorName: data.authorName || '',
      authorEmail: data.authorEmail,
      isInternal: data.isInternal || false,
      content: data.content || '',
      attachments: data.attachments || [],
      createdAt: data.createdAt?.toDate() || new Date(),
    };
  }
}

